import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAllSportsAdmin, updateSport } from "../../services/api/sportsService";
import FloatingButton from "../ui/FloatingButton";
import "../../styles/features/sports.css";

const CATEGORIES = [
  "Team Sports",
  "Individual Sports",
  "Water Sports",
  "Combat Sports",
  "Racquet Sports",
  "Outdoor Sports",
  "Indoor Sports",
  "Winter Sports",
  "Athletics",
  "Fitness",
  "Other",
];

const SportsAdminEdit = ({ token }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    rules: "",
    equipment: "",
    min_players: 1,
    max_players: 10,
    image_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");

  // Load current sport data to prefill the form
  useEffect(() => {
    const fetchSport = async () => {
      try {
        setFetchLoading(true);
        const response = await getAllSportsAdmin();
        const sports = response.data || [];
        const sport = sports.find((s) => String(s.id) === String(id));
        if (!sport) {
          setError("Sport not found.");
          return;
        }
        setFormData({
          name: sport.name || "",
          description: sport.description || "",
          category: sport.category || "",
          rules: sport.rules || "",
          equipment: sport.equipment || "",
          min_players: sport.min_players || sport.minPlayers || 1,
          max_players: sport.max_players || sport.maxPlayers || 10,
          image_url: sport.image_url || sport.imageUrl || "",
        });
      } catch (err) {
        setError("Failed to load sport: " + (err.response?.data?.message || err.message));
      } finally {
        setFetchLoading(false);
      }
    };
    if (id) fetchSport();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "min_players" || name === "max_players"
          ? parseInt(value) || 1
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setLoading(true);
      await updateSport(token, id, formData);
      navigate("/sports-admin", {
        state: { successMessage: `Sport "${formData.name}" updated successfully!` },
      });
    } catch (err) {
      setError(
        "Failed to update sport: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => navigate("/sports-admin");

  return (
    <div className="sport-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit Sport">
      <div className="sport-modal-backdrop" onClick={handleClose} />

      <div className="sport-modal-panel">
        {/* Header */}
        <div className="sport-modal-header">
          <div className="sport-modal-title-row">
            <h2 className="sport-modal-title">✏️ Edit Sport</h2>
            <button
              type="button"
              className="sport-modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p className="sport-modal-subtitle">
            Update the details for this sport.
          </p>
        </div>

        {/* Scrollable body */}
        <div className="sport-modal-body">
          {fetchLoading ? (
            <div className="sport-modal-loading">
              <div className="loading-spinner-gaming"></div>
              <p>Loading sport details…</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="gaming-message-floating error" style={{ marginBottom: "1rem" }}>
                  <span className="message-icon">❌</span>
                  <span className="message-text">{error}</span>
                </div>
              )}

              <form id="edit-sport-form" onSubmit={handleSubmit} className="sport-modal-form">
                {/* Row 1 — Name + Category */}
                <div className="sport-modal-row">
                  <div className="form-group-gaming">
                    <label htmlFor="se-name">🏷️ Sport Name *</label>
                    <input
                      id="se-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="gaming-input"
                      placeholder="e.g., Football, Basketball"
                      required
                    />
                  </div>
                  <div className="form-group-gaming">
                    <label htmlFor="se-category">📂 Category *</label>
                    <select
                      id="se-category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="gaming-select"
                      required
                    >
                      <option value="">Select Category</option>
                      {CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2 — Min / Max players */}
                <div className="sport-modal-row">
                  <div className="form-group-gaming">
                    <label htmlFor="se-min">👤 Min Players</label>
                    <input
                      id="se-min"
                      type="number"
                      name="min_players"
                      value={formData.min_players}
                      onChange={handleChange}
                      className="gaming-input"
                      min="1"
                      placeholder="Minimum players"
                    />
                  </div>
                  <div className="form-group-gaming">
                    <label htmlFor="se-max">👥 Max Players</label>
                    <input
                      id="se-max"
                      type="number"
                      name="max_players"
                      value={formData.max_players}
                      onChange={handleChange}
                      className="gaming-input"
                      min="1"
                      placeholder="Maximum players"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="form-group-gaming">
                  <label htmlFor="se-desc">📖 Description</label>
                  <textarea
                    id="se-desc"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="gaming-textarea"
                    placeholder="Describe the sport, its objectives and gameplay..."
                    rows="3"
                  />
                </div>

                {/* Rules */}
                <div className="form-group-gaming">
                  <label htmlFor="se-rules">📋 Rules</label>
                  <textarea
                    id="se-rules"
                    name="rules"
                    value={formData.rules}
                    onChange={handleChange}
                    className="gaming-textarea"
                    placeholder="Basic rules and regulations..."
                    rows="3"
                  />
                </div>

                {/* Equipment */}
                <div className="form-group-gaming">
                  <label htmlFor="se-equip">🛠️ Equipment</label>
                  <textarea
                    id="se-equip"
                    name="equipment"
                    value={formData.equipment}
                    onChange={handleChange}
                    className="gaming-textarea"
                    placeholder="Required equipment and gear..."
                    rows="2"
                  />
                </div>

                {/* Image URL */}
                <div className="form-group-gaming">
                  <label htmlFor="se-img">🖼️ Image URL</label>
                  <input
                    id="se-img"
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    className="gaming-input"
                    placeholder="https://example.com/sport-image.jpg"
                  />
                </div>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sport-modal-footer">
          <FloatingButton
            type="button"
            variant="outline"
            size="medium"
            onClick={handleClose}
            icon="❌"
          >
            <span className="btn-text">Cancel</span>
          </FloatingButton>
          <FloatingButton
            type="submit"
            form="edit-sport-form"
            variant="success"
            size="medium"
            disabled={loading || fetchLoading}
            icon={loading ? "⏳" : "💾"}
          >
            <span className="btn-text">{loading ? "Saving..." : "Save Changes"}</span>
          </FloatingButton>
        </div>
      </div>
    </div>
  );
};

export default SportsAdminEdit;
